import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { orderBy } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import { useCouple } from "../hooks/useCouple";
import { useCoupleCollection } from "../hooks/useCoupleCollection";
import { Fab } from "../components/Fab";
import { Sheet } from "../components/Sheet";
import type { Note } from "../types";

export function Notes() {
  const { user } = useAuth();
  const { couple, profile } = useCouple();
  const { items, loading, add, patch, remove } = useCoupleCollection<Note>(
    couple?.id ?? null,
    "notes",
    orderBy("updatedAt", "desc")
  );
  const [text, setText] = useState("");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function openAdd() {
    setEditingId(null);
    setText("");
    setSheetOpen(true);
  }

  function openEdit(note: Note) {
    setEditingId(note.id);
    setText(note.text);
    setSheetOpen(true);
  }

  // Arriving from the Neuigkeiten bell (?open=id) — open that note's edit
  // sheet directly once it's actually loaded.
  const [searchParams] = useSearchParams();
  const [pendingOpenId, setPendingOpenId] = useState(() => searchParams.get("open"));
  useEffect(() => {
    if (!pendingOpenId) return;
    const match = items.find((n) => n.id === pendingOpenId);
    if (match) {
      openEdit(match);
      setPendingOpenId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingOpenId]);

  function closeSheet() {
    setSheetOpen(false);
    setEditingId(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting || !text.trim() || !user || !profile) return;
    setSubmitting(true);
    try {
      if (editingId) {
        await patch(editingId, { text: text.trim(), updatedByUid: user.uid, updatedByName: profile.displayName }, "updatedAt");
      } else {
        await add({ text: text.trim(), updatedByUid: user.uid, updatedByName: profile.displayName }, "updatedAt");
      }
      closeSheet();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-28 pt-6">
      <h1 className="font-display text-[1.75rem] font-semibold tracking-tight text-plum-900 dark:text-night-accent">Notizen</h1>
      <p className="mt-1 text-sm text-ink-400 dark:text-night-faint">Alles, was sonst nirgends hinpasst.</p>

      {loading ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Lädt…</p>
      ) : items.length === 0 ? (
        <p className="mt-6 text-sm text-ink-400 dark:text-night-faint">Noch keine Notizen.</p>
      ) : (
        <ul className="mt-5 space-y-3">
          {items.map((n) => (
            <li key={n.id} className="card flex items-start justify-between gap-3 rounded-2xl p-4">
              <button onClick={() => openEdit(n)} className="min-w-0 flex-1 text-left">
                <p className="whitespace-pre-wrap text-sm text-ink-900 dark:text-night-text">{n.text}</p>
                <p className="mt-1.5 text-xs text-ink-400 dark:text-night-faint">von {n.updatedByName}</p>
              </button>
              <div className="flex flex-shrink-0 items-center gap-2.5">
                <button onClick={() => openEdit(n)} aria-label="Bearbeiten" className="text-ink-400 dark:text-night-faint">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
                    <path d="M16.5 4.5 19.5 7.5 8 19H5v-3L16.5 4.5Z" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button onClick={() => remove(n.id)} aria-label="Löschen" className="text-ink-400 dark:text-night-faint">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4.5 w-4.5">
                    <path d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m-9 0 1 13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1l1-13" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Fab onClick={openAdd} label="Notiz hinzufügen" />

      <Sheet open={sheetOpen} onClose={closeSheet} title={editingId ? "Notiz bearbeiten" : "Neue Notiz"}>
        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            placeholder="Neue Notiz…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            className="w-full rounded-xl border border-cream-200 bg-white px-4 py-3 text-base text-ink-900 outline-none focus:border-plum-600 dark:border-night-border dark:bg-night-surface-raised dark:text-night-text"
          />
          <button
            type="submit"
            disabled={!text.trim() || submitting}
            className="h-12 w-full rounded-xl bg-plum-700 text-sm font-semibold text-cream-50 disabled:opacity-50"
          >
            {submitting ? "Wird gespeichert…" : editingId ? "Änderungen speichern" : "Notiz speichern"}
          </button>
        </form>
      </Sheet>
    </main>
  );
}
